// ============================================================================
// WRITE EXPORT AUDIT — Non-PHI audit log for receipt export actions
//
// Writes a single doc to:
//   tenants/{tenantId}/referrals/{referralId}/auditLogs/{logId}
//
// INVARIANT: This log contains ZERO PHI. Only eventType + actor + timestamps.
//
// Event types:
//   RECEIPT_COPIED_NO_PHI    — "Copy EHR Note" clicked
//   RECEIPT_PDF_DOWNLOADED_PHI — "Download Full Receipt PDF" clicked
//
// These are advisory audit events (best-effort). If write fails, it is
// silently ignored — the export action itself should not be blocked by a
// failed audit write.
// ============================================================================

import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { PATHS } from '../firestorePaths.js';

export const EXPORT_AUDIT_EVENTS = {
  COPIED_NO_PHI:         'RECEIPT_COPIED_NO_PHI',
  PDF_DOWNLOADED_PHI:    'RECEIPT_PDF_DOWNLOADED_PHI',
};

/**
 * Write a non-PHI export audit event. Silently swallows errors.
 *
 * @param {object} params
 * @param {string} params.tenantId
 * @param {string} params.referralId
 * @param {string} params.actorUid
 * @param {string} params.actorRole
 * @param {string} params.eventType  — one of EXPORT_AUDIT_EVENTS values
 * @returns {Promise<void>}  — always resolves (never rejects)
 */
export const writeExportAudit = async ({ tenantId, referralId, actorUid, actorRole, eventType }) => {
  try {
    const db = getFirestore(getApp());
    const logId = `EXPORT_${Date.now().toString(36).toUpperCase()}_${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
    const docPath = PATHS.referralAuditLog(tenantId, referralId, logId);
    await setDoc(doc(db, docPath), {
      tenantId,
      referralId,
      actorUid:     actorUid  || 'unknown',
      actorRole:    actorRole || 'unknown',
      // createdByUid mirrors actorUid — required by Firestore auditLogs create rule
      createdByUid: actorUid  || 'unknown',
      eventType,
      createdAt:    serverTimestamp(),
      schemaVersion: 1,
    });
  } catch (err) {
    // Advisory only — never block the export action
    console.warn('[writeExportAudit] Failed to write export audit event:', err?.message);
  }
};
